import React, { useState } from 'react';
import { Copy, Check, Link2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

export interface ActionLinkModalProps {
  open: boolean;
  title: string;
  link: string;
  onClose: () => void;
}

// A copy-once modal for admin-generated action links (password reset / invite).
// Email delivery is unreliable until SES production access, so these single-use
// links are handed over manually. Read-only monospace field (select-all on
// focus) + Copy button; the warning makes the single-use/expiry posture explicit.
// Rides the canonical DS modal shell (ui/dialog.tsx): scrim var(--overlay),
// panel var(--surface-raised), var(--shadow-modal), focus-trap, Esc + its own
// close affordance.
export function ActionLinkModal({ open, title, link, onClose }: ActionLinkModalProps) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent aria-label={title} style={{ maxWidth: 520 }}>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700 }}>
          <Link2 size={16} /> {title}
        </DialogTitle>

        <div style={{ padding: '10px 12px', background: 'var(--ps-pill-amber-bg)', borderRadius: 8, fontSize: 13, color: 'var(--ps-pill-amber-tx)' }}>
          Works once and expires. Share over a trusted channel.
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {/* ps-no-capture: rrweb blockClass. The link is single-use credential
              material; maskAllInputs already hides the value, this fences the
              node outright so no capture posture can render it. */}
          <input
            className="ps-no-capture"
            readOnly
            value={link}
            aria-label="Action link"
            onFocus={(e) => e.target.select()}
            style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontFamily: 'monospace', fontSize: 12, outline: 'none', color: 'var(--text-primary)', background: 'var(--bg-secondary)', minWidth: 0 }}
          />
          <button
            onClick={copy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
